<template>
	<div class="container">
		<Form @submit="onSubmit">
			<div :class="['form-fields', {'keep-me-updated': keepMeUpdated }]">
				<div class="form-field" v-for="(field, index) in fields">
					<template v-if="field.fields.fieldType !== 'submit' && field?.sys?.contentType?.sys?.id === 'formField'">
						<label
							v-if="!(keepMeUpdated && field.fields.fieldType === 'textarea')"
							:for="field.fields.fieldID"
							class="form-label-wrapper"
							:class="{
							'is-focused': isFocused[field.sys.id],
							'has-value': hasValue[field.sys.id]
						}"
						>
							<div class="form-label">
								{{ field.fields.fieldName }}
								<template v-if="field.fields.required">*</template>
							</div>
							<div class="form-input">
								<Field
									v-if="!(keepMeUpdated && field.fields.fieldType === 'textarea')"
									:name="field.fields.fieldID"
									:type="field.fields.fieldType"
									:rules="(value) => {
									  // Only validate if field has a value or is required
									  if (!value) {
										return field.fields.required ? field.fields.fieldErrorMessage : true;
									  }

									  // Has value, validate it
									  return validateField(value, field.fields);
									}"
									:as="field.fields.fieldType !== 'textarea' ? 'input' : 'textarea'"
									v-maska="field.fields.fieldID === 'phone' ? '(###) ###-####' : null"
									@focus="(value) => setFocus(field.sys.id, true, value)"
									@blur="(value) => setFocus(field.sys.id, false, value)"
								/>
							</div>

							<Transition name="fade-opacity">
								<ErrorMessage :name="field.fields.fieldID" class="error-message" />
							</Transition>
						</label>
					</template>

					<template v-if="field.fields.fieldType && field.fields.fieldType === 'checkbox'">
						<label>
							<Field :name="field.fields.fieldID" :type="field.fields.fieldType" :id="field.fields.fieldID" value="true" />
							{{ field.fields.fieldName }}
						</label>
						<ErrorMessage name="terms"  class="error-message" />
					</template>
					<template v-if="field.fields.fieldType === 'submit'">
						<div class="consent-text">
							{{ $t("form.accept")}}
							<NuxtLink :to="$t('pages.privacy-policy.url')">
								{{ $t('pages.privacy-policy.title') }}
							</NuxtLink>.
						</div>
						<Button v-if="field.fields?.cta" :data="field.fields?.cta?.fields" :type="`submit`"></Button>
					</template>
				</div>
			</div>
			<div v-if="formSuccessMessage" :class="['form-message', {'show-form-message': showFormMessage }]">
				<RichText v-if="!formHasError && formSuccessMessage?.fields?.successMessage?.content" :content="formSuccessMessage?.fields?.successMessage?.content" />
				<RichText v-if="formHasError && formErrorMessage?.fields?.errorMessage?.content" :content="formErrorMessage?.fields?.errorMessage?.content" />
			</div>
		</Form>
	</div>
</template>

<script setup>
import { vMaska } from "maska/vue";
import { Form, Field, ErrorMessage, useForm } from 'vee-validate';
const { t, locale } = useI18n();
const { $eventBus } = useNuxtApp();
import { useRoute } from 'vue-router';

const  { data } = defineProps({
	data: {
		type: Object,
		required: true
	}
});

const route = useRoute();
const fields = computed(() => data?.data.fields ?? {});
const keepMeUpdated = computed(() => {
	return route.query.keepmeupdated === 'true';
});
const formSuccessMessage = computed(() => {
	return fields.value?.find(
		field => field?.sys?.contentType?.sys?.id === 'formSuccessMessage'
	);
});
const formErrorMessage = computed(() => {
	return fields.value?.find(
		field => field?.sys?.contentType?.sys?.id === 'formErrorMessage'
	);
});

const isFocused = ref({});
const hasValue = ref({});
const showFormMessage = ref(false);
const formHasError = ref(false);
const setFocus = (id, value, data) => {
	isFocused.value[id] = value;
	hasValue.value[id] = value;

	if(data.target.value) {
		hasValue.value[id] = true;
	}
};

const validateField = (value, field) => {
	// if the field is empty
	if (!value) {
		return field?.fieldErrorMessage || t('form.required');
	}

	if(field.fieldType === 'email') {
		const regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
		if (!regex.test(value)) {

			return t('form.invalid_email');
		}
	}

	if(field.fieldType === 'tel') {
		const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;

		if (value && !phoneRegex.test(value)) {
			return field?.fieldErrorMessage || t('form.required');
		}

		return true;
	}

	// All is good
	return true;
};

const onSubmit = async(values) => {
	$eventBus.emit('loader:show', true);

	values.lang = locale.value;
	values.keepMeUpdated = keepMeUpdated.value;

	try {
		await $fetch('/api/email/create-contact', {
			method: 'POST',
			body: values,
		});

		await $fetch('/api/email/send-email', {
			method: 'POST',
			body: values,
		});

		await $fetch('/api/email/send-email-to-user', {
			method: 'POST',
			body: values,
		});

		showFormMessage.value = true;
		keepMeUpdated.value = false;
		$eventBus.emit('loader:show', false);
	} catch (err) {
		formHasError.value = true;
		showFormMessage.value = true;
		$eventBus.emit('loader:show', false);

		console.error(err);
	}
};

const closeFormMessage = () => {
	showFormMessage.value = false;
};
</script>

<style lang="scss">
@use '~/assets/scss/components/Form.scss';
</style>